/**
 * FishAudioAdminPanel.jsx
 * Super-admin panel for Fish Audio: config, voices, live TTS test, usage stats & credit monitoring.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Headphones, RefreshCw, Check, AlertCircle, Settings, Play, Square,
  BarChart3, Mic, Globe, Zap, DollarSign, TrendingUp, Clock,
  Volume2, VolumeX, Search, ChevronDown, ChevronUp, Save, Eye, EyeOff,
  Activity, Star, Info, ExternalLink, Copy
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ── Tiny helpers ──────────────────────────────────────────────────────────────
const fmt = (n, d = 2) => Number(n || 0).toFixed(d);
const fmtNum = (n) => Number(n || 0).toLocaleString();
const StatCard = ({ icon: Icon, label, value, sub, color = 'blue' }) => {
  const colors = {
    blue: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    green: 'text-green-400 bg-green-500/10 border-green-500/20',
    red: 'text-red-400 bg-red-500/10 border-red-500/20',
    yellow: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
    purple: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
  };
  return (
    <div className={`rounded-2xl border p-5 ${colors[color]}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center bg-current/10`}>
          <Icon size={18} className="opacity-80" />
        </div>
        <span className="text-xs font-semibold uppercase tracking-widest opacity-60">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      {sub && <p className="text-xs mt-1 opacity-50">{sub}</p>}
    </div>
  );
};

// ── Mini bar-chart component ──────────────────────────────────────────────────
const MiniBarChart = ({ data, field, color = '#3b82f6', label }) => {
  if (!data?.length) return null;
  const max = Math.max(...data.map(d => d[field] || 0), 1);
  return (
    <div>
      <p className="text-xs text-gray-500 mb-2">{label}</p>
      <div className="flex items-end gap-0.5 h-16">
        {data.slice(-30).map((d, i) => (
          <div
            key={i}
            className="flex-1 rounded-t transition-all"
            style={{
              height: `${Math.max(2, ((d[field] || 0) / max) * 100)}%`,
              background: color,
              opacity: 0.7,
              minWidth: 2,
            }}
            title={`${d.date}: ${d[field]}`}
          />
        ))}
      </div>
      <div className="flex justify-between text-xs text-gray-600 mt-1">
        <span>{data[0]?.date?.slice(5)}</span>
        <span>{data[data.length - 1]?.date?.slice(5)}</span>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
export default function FishAudioAdminPanel() {
  const [tab, setTab] = useState('status');
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  // Voices tab
  const [voices, setVoices] = useState([]);
  const [voiceSearch, setVoiceSearch] = useState('');
  const [voiceLang, setVoiceLang] = useState('');
  const [voicePage, setVoicePage] = useState(1);
  const [voiceTotal, setVoiceTotal] = useState(0);
  const [loadingVoices, setLoadingVoices] = useState(false);

  // Test tab
  const [testText, setTestText] = useState('Hola, soy OkBot. Estoy listo para ayudarte con lo que necesites hoy.');
  const [testLang, setTestLang] = useState('es');
  const [testVoice, setTestVoice] = useState('default');
  const [testLatency, setTestLatency] = useState('normal');
  const [testFmt, setTestFmt] = useState('mp3');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const blobUrlRef = useRef(null);

  // Usage tab
  const [usage, setUsage] = useState(null);
  const [usageDays, setUsageDays] = useState(30);
  const [loadingUsage, setLoadingUsage] = useState(false);

  // Config tab
  const [config, setConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);

  // ── Load status on mount ───────────────────────────────────────────────────
  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const r = await axios.get(`${API}/admin/fish-audio/status`);
      setStatus(r.data);
    } catch {
      setStatus({ configured: false, error: 'No se pudo conectar' });
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // ── Load voices ────────────────────────────────────────────────────────────
  const loadVoices = useCallback(async () => {
    setLoadingVoices(true);
    try {
      const r = await axios.get(`${API}/admin/fish-audio/voices`, {
        params: { page_size: 18, page_number: voicePage, language: voiceLang, search: voiceSearch },
      });
      setVoices(r.data.voices || []);
      setVoiceTotal(r.data.total || 0);
    } catch { toast.error('Error cargando voces'); }
    finally { setLoadingVoices(false); }
  }, [voicePage, voiceLang, voiceSearch]);

  useEffect(() => { if (tab === 'voices') loadVoices(); }, [tab, loadVoices]);

  // ── Load usage stats ───────────────────────────────────────────────────────
  const loadUsage = useCallback(async () => {
    setLoadingUsage(true);
    try {
      const r = await axios.get(`${API}/admin/fish-audio/usage`, { params: { days: usageDays } });
      setUsage(r.data);
    } catch { toast.error('Error cargando estadísticas'); }
    finally { setLoadingUsage(false); }
  }, [usageDays]);

  useEffect(() => { if (tab === 'usage') loadUsage(); }, [tab, loadUsage]);

  // ── Load config ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (tab !== 'config') return;
    axios.get(`${API}/admin/fish-audio/config`)
      .then(r => setConfig(r.data))
      .catch(() => setConfig({ default_voice_id: 'default', default_latency: 'normal', default_format: 'mp3', max_chars: 3000, notes: '' }));
  }, [tab]);

  // ── Test TTS ───────────────────────────────────────────────────────────────
  const runTest = async () => {
    if (!testText.trim()) return;
    setTesting(true); setTestResult(null);
    // Stop previous
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
    setPlaying(false);
    try {
      const r = await axios.post(`${API}/admin/fish-audio/test`, {
        text: testText.trim(),
        language: testLang,
        voice_id: testVoice,
        latency: testLatency,
        format: testFmt,
      });
      setTestResult(r.data);
      if (r.data.success && r.data.audio_url) {
        toast.success(`Audio generado · ${r.data.chars} chars · ~$${fmt(r.data.cost_usd_estimate, 5)}`);
      } else {
        toast.error(r.data.error || 'Error generando audio');
      }
    } catch (e) {
      toast.error('Error de conexión');
      setTestResult({ success: false, error: e.message });
    } finally {
      setTesting(false);
    }
  };

  const playTestAudio = () => {
    if (!testResult?.audio_url) return;
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; setPlaying(false); return; }
    // Convert base64 → Blob URL
    const dataUrl = testResult.audio_url;
    const b64 = dataUrl.split(',')[1];
    const mime = dataUrl.split(';')[0].split(':')[1] || 'audio/mpeg';
    const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: mime });
    const url = URL.createObjectURL(blob);
    blobUrlRef.current = url;
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onended = () => { setPlaying(false); URL.revokeObjectURL(url); blobUrlRef.current = null; audioRef.current = null; };
    audio.onerror = () => { setPlaying(false); toast.error('Error reproduciendo audio'); };
    audio.play().then(() => setPlaying(true)).catch(() => toast.error('Autoplay bloqueado — intenta de nuevo'));
  };

  // ── Save config ────────────────────────────────────────────────────────────
  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.post(`${API}/admin/fish-audio/config`, config);
      toast.success('Configuración guardada');
    } catch { toast.error('Error guardando configuración'); }
    finally { setSavingConfig(false); }
  };

  // ─────────────────────────────────────────────────────────────────────────
  const TABS = [
    { id: 'status', label: 'Estado & Cuenta', icon: Activity },
    { id: 'test',   label: 'Probar TTS',       icon: Mic },
    { id: 'voices', label: 'Biblioteca de Voces', icon: Volume2 },
    { id: 'usage',  label: 'Uso & Costos',     icon: BarChart3 },
    { id: 'config', label: 'Configuración',    icon: Settings },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="glass-panel p-6 rounded-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500/30 to-yellow-500/20 border border-orange-500/30 flex items-center justify-center">
              <Headphones size={22} className="text-orange-400" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">Fish Audio · Panel de Control</h2>
              <p className="text-gray-500 text-sm">Voz AI profesional · TTS & gestión de créditos</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {status?.configured && !status?.credits_empty && !status?.error && (
              <span className="flex items-center gap-1.5 text-xs text-green-400 bg-green-500/10 border border-green-500/20 px-3 py-1.5 rounded-full font-medium">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                Operacional
              </span>
            )}
            {status?.credits_empty && (
              <span className="flex items-center gap-1.5 text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 px-3 py-1.5 rounded-full font-medium">
                <AlertCircle size={12} /> Sin créditos
              </span>
            )}
            <button onClick={loadStatus} disabled={loadingStatus}
              className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 transition-all">
              <RefreshCw size={16} className={loadingStatus ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                tab === t.id
                  ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
              }`}>
              <Icon size={15} />{t.label}
            </button>
          );
        })}
      </div>

      {/* ── STATUS TAB ─────────────────────────────────────────────────────── */}
      {tab === 'status' && (
        <div className="space-y-5">
          {loadingStatus ? (
            <div className="flex justify-center py-16"><RefreshCw size={24} className="animate-spin text-orange-400" /></div>
          ) : !status?.key_present ? (
            <div className="glass-panel p-8 rounded-2xl text-center">
              <AlertCircle size={40} className="text-yellow-400 mx-auto mb-4" />
              <h3 className="text-white font-semibold mb-2">API Key no configurada</h3>
              <p className="text-gray-500 text-sm mb-4">Agrega <code className="text-orange-400 bg-orange-500/10 px-2 py-0.5 rounded">FISH_AUDIO_API_KEY</code> en los Secrets de tu proyecto.</p>
              <a href="https://fish.audio/app/developers" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600 transition-colors">
                <ExternalLink size={14} /> Obtener API Key en fish.audio
              </a>
            </div>
          ) : (
            <>
              {/* Account info */}
              {status.account && (
                <div className="glass-panel p-6 rounded-2xl">
                  <h3 className="text-white font-semibold mb-5 flex items-center gap-2"><Globe size={16} className="text-orange-400" />Cuenta Fish Audio</h3>
                  <div className="flex items-center gap-5 mb-5">
                    {status.account.avatar && (
                      <img src={status.account.avatar} alt="avatar" className="w-14 h-14 rounded-full border border-white/10 object-cover" />
                    )}
                    <div>
                      <p className="text-white font-semibold text-lg">{status.account.username || status.account.email}</p>
                      <p className="text-gray-500 text-sm">{status.account.email}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/5 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-green-400">${fmt(status.account.balance_usd)}</p>
                      <p className="text-gray-500 text-xs mt-1">Balance USD</p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-orange-400">
                        {status.account.balance_usd > 0 ? '✓' : '⚠'}
                      </p>
                      <p className="text-gray-500 text-xs mt-1">
                        {status.account.balance_usd > 0 ? 'Créditos disponibles' : 'Sin créditos'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Credit empty warning */}
              {status.credits_empty && (
                <div className="glass-panel p-6 rounded-2xl border border-yellow-500/20 bg-yellow-500/5">
                  <div className="flex items-start gap-4">
                    <AlertCircle size={22} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-yellow-400 font-semibold mb-1">Sin créditos API</h3>
                      <p className="text-gray-400 text-sm mb-3">
                        El API Key está configurada correctamente, pero la cuenta no tiene créditos suficientes.<br />
                        Los créditos API se gestionan independientemente de la plataforma web.
                      </p>
                      <a href="https://fish.audio/app/developers" target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black rounded-xl text-sm font-semibold hover:bg-yellow-400 transition-colors">
                        <ExternalLink size={13} /> Recargar créditos en fish.audio
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* API info */}
              <div className="glass-panel p-5 rounded-2xl">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Info size={15} className="text-gray-400" />Información de la API</h3>
                <div className="space-y-3 text-sm">
                  {[
                    { label: 'Endpoint TTS', value: 'https://api.fish.audio/v1/tts', copy: true },
                    { label: 'Endpoint Voces', value: 'https://api.fish.audio/v1/model', copy: true },
                    { label: 'Precio estimado', value: '~$0.015 USD por 1,000 caracteres' },
                    { label: 'Latencias', value: 'normal · balanced' },
                    { label: 'Formatos', value: 'MP3 · WAV · Opus · FLAC' },
                    { label: 'Limite chars/req', value: '4,000 caracteres' },
                  ].map(row => (
                    <div key={row.label} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
                      <span className="text-gray-500">{row.label}</span>
                      <div className="flex items-center gap-2">
                        <code className="text-orange-400 text-xs">{row.value}</code>
                        {row.copy && (
                          <button onClick={() => { navigator.clipboard.writeText(row.value); toast.success('Copiado'); }}
                            className="p-1 rounded hover:bg-white/10 text-gray-600 hover:text-gray-300 transition-colors">
                            <Copy size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── TEST TTS TAB ───────────────────────────────────────────────────── */}
      {tab === 'test' && (
        <div className="space-y-5">
          <div className="glass-panel p-6 rounded-2xl">
            <h3 className="text-white font-semibold mb-5 flex items-center gap-2"><Mic size={16} className="text-orange-400" />Probar Text-to-Speech</h3>

            <div className="space-y-4">
              {/* Text input */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Texto a narrar <span className="text-gray-600">({testText.length}/1000)</span></label>
                <textarea
                  value={testText}
                  onChange={e => setTestText(e.target.value.slice(0, 1000))}
                  rows={4}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm resize-none focus:outline-none focus:border-orange-500/50 transition-colors"
                  placeholder="Escribe el texto que quieres convertir a voz…"
                />
              </div>

              {/* Options row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Idioma', value: testLang, set: setTestLang, opts: [
                    ['auto', 'Auto'], ['es', '🇪🇸 Español'], ['en', '🇺🇸 English'],
                    ['pt', '🇧🇷 Português'], ['fr', '🇫🇷 Français'], ['de', '🇩🇪 Deutsch'],
                    ['ja', '🇯🇵 日本語'], ['zh', '🇨🇳 中文'], ['ko', '🇰🇷 한국어'],
                  ]},
                  { label: 'Latencia', value: testLatency, set: setTestLatency, opts: [['normal', 'Normal'], ['balanced', 'Balanced']] },
                  { label: 'Formato', value: testFmt, set: setTestFmt, opts: [['mp3', 'MP3'], ['wav', 'WAV'], ['opus', 'Opus'], ['flac', 'FLAC']] },
                  { label: 'Voice ID (opcional)', value: testVoice, isText: true, set: setTestVoice },
                ].map(opt => (
                  <div key={opt.label}>
                    <label className="block text-gray-500 text-xs mb-1.5">{opt.label}</label>
                    {opt.isText ? (
                      <input
                        value={opt.value}
                        onChange={e => opt.set(e.target.value)}
                        placeholder="default"
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500/50"
                      />
                    ) : (
                      <select value={opt.value} onChange={e => opt.set(e.target.value)}
                        className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500/50">
                        {opt.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    )}
                  </div>
                ))}
              </div>

              {/* Generate button */}
              <button onClick={runTest} disabled={testing || !testText.trim()}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-semibold transition-all">
                {testing ? <RefreshCw size={16} className="animate-spin" /> : <Zap size={16} />}
                {testing ? 'Generando audio…' : 'Generar voz con Fish Audio'}
              </button>
            </div>
          </div>

          {/* Result */}
          {testResult && (
            <div className={`glass-panel p-6 rounded-2xl border ${testResult.success ? 'border-green-500/20' : 'border-red-500/20'}`}>
              {testResult.success ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                        <Check size={16} className="text-green-400" />
                      </div>
                      <div>
                        <p className="text-green-400 font-semibold">Audio generado correctamente</p>
                        <p className="text-gray-500 text-xs">{fmtNum(testResult.chars)} chars · costo est. ${fmt(testResult.cost_usd_estimate, 5)}</p>
                      </div>
                    </div>
                    <button onClick={playTestAudio}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                        playing
                          ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                          : 'bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30'
                      }`}>
                      {playing ? <><Square size={14} /> Detener</> : <><Play size={14} /> Reproducir</>}
                    </button>
                  </div>
                  {playing && (
                    <div className="flex items-center gap-2">
                      <div className="flex gap-0.5">
                        {[...Array(12)].map((_, i) => (
                          <div key={i} className="w-1 bg-orange-400 rounded-full animate-pulse"
                            style={{ height: `${8 + Math.random() * 16}px`, animationDelay: `${i * 80}ms` }} />
                        ))}
                      </div>
                      <span className="text-orange-400 text-sm">Reproduciendo…</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-400 font-semibold">Error al generar audio</p>
                    <p className="text-gray-400 text-sm mt-1">{testResult.error}</p>
                    {testResult.tip && <p className="text-yellow-400 text-xs mt-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">{testResult.tip}</p>}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── VOICES TAB ─────────────────────────────────────────────────────── */}
      {tab === 'voices' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-48">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input value={voiceSearch} onChange={e => { setVoiceSearch(e.target.value); setVoicePage(1); }}
                placeholder="Buscar voz…"
                className="w-full pl-9 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-orange-500/50" />
            </div>
            <select value={voiceLang} onChange={e => { setVoiceLang(e.target.value); setVoicePage(1); }}
              className="bg-[#111] border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-orange-500/50">
              <option value="">Todos los idiomas</option>
              {['es', 'en', 'pt', 'fr', 'de', 'it', 'ja', 'zh', 'ko', 'ar', 'ru'].map(l =>
                <option key={l} value={l}>{l.toUpperCase()}</option>
              )}
            </select>
            <button onClick={loadVoices} disabled={loadingVoices}
              className="flex items-center gap-2 px-4 py-2.5 bg-orange-500/15 hover:bg-orange-500/25 text-orange-400 border border-orange-500/25 rounded-xl text-sm transition-all">
              <RefreshCw size={14} className={loadingVoices ? 'animate-spin' : ''} />
              Buscar
            </button>
          </div>

          <p className="text-gray-600 text-xs">{fmtNum(voiceTotal)} voces encontradas</p>

          {loadingVoices ? (
            <div className="flex justify-center py-12"><RefreshCw size={22} className="animate-spin text-orange-400" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {voices.map(v => (
                <div key={v.id} className="glass-panel p-4 rounded-2xl hover:border-orange-500/20 transition-all cursor-pointer group">
                  <div className="flex items-start gap-3 mb-3">
                    {v.cover_image ? (
                      <img src={v.cover_image} alt={v.title} className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                        <Volume2 size={18} className="text-orange-400" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-white font-medium text-sm truncate">{v.title}</p>
                      <p className="text-gray-500 text-xs">{v.author || 'Official'}</p>
                    </div>
                    {v.is_official && (
                      <span className="flex-shrink-0 text-xs bg-orange-500/20 text-orange-400 border border-orange-500/25 px-2 py-0.5 rounded-full">Official</span>
                    )}
                  </div>
                  {v.description && (
                    <p className="text-gray-600 text-xs line-clamp-2 mb-3">{v.description}</p>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex gap-1 flex-wrap">
                      {(v.language || []).slice(0, 3).map(l => (
                        <span key={l} className="text-xs bg-white/5 text-gray-500 px-2 py-0.5 rounded-full">{l}</span>
                      ))}
                    </div>
                    <button onClick={() => {
                      navigator.clipboard.writeText(v.id);
                      toast.success(`Voice ID copiado: ${v.id.slice(0, 8)}…`);
                    }}
                      className="opacity-0 group-hover:opacity-100 flex items-center gap-1 text-xs text-orange-400 hover:text-orange-300 transition-all">
                      <Copy size={11} /> Copiar ID
                    </button>
                  </div>
                  {v.score > 0 && (
                    <div className="flex items-center gap-1 mt-2">
                      <Star size={11} className="text-yellow-400 fill-yellow-400" />
                      <span className="text-xs text-gray-500">{fmt(v.score, 1)} · {fmtNum(v.task_count)} usos</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {voiceTotal > 18 && (
            <div className="flex items-center justify-center gap-2">
              <button disabled={voicePage <= 1} onClick={() => setVoicePage(p => p - 1)}
                className="px-4 py-2 rounded-xl bg-white/5 text-gray-400 disabled:opacity-30 hover:bg-white/10 text-sm transition-all">Anterior</button>
              <span className="text-gray-500 text-sm">Página {voicePage}</span>
              <button disabled={voicePage * 18 >= voiceTotal} onClick={() => setVoicePage(p => p + 1)}
                className="px-4 py-2 rounded-xl bg-white/5 text-gray-400 disabled:opacity-30 hover:bg-white/10 text-sm transition-all">Siguiente</button>
            </div>
          )}
        </div>
      )}

      {/* ── USAGE TAB ──────────────────────────────────────────────────────── */}
      {tab === 'usage' && (
        <div className="space-y-5">
          {/* Period selector */}
          <div className="flex items-center gap-3">
            <span className="text-gray-500 text-sm">Período:</span>
            {[7, 14, 30, 90].map(d => (
              <button key={d} onClick={() => setUsageDays(d)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                  usageDays === d ? 'bg-orange-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}>{d}d</button>
            ))}
            <button onClick={loadUsage} disabled={loadingUsage}
              className="ml-auto flex items-center gap-2 px-3 py-1.5 bg-white/5 text-gray-400 rounded-lg text-sm hover:bg-white/10 transition-all">
              <RefreshCw size={13} className={loadingUsage ? 'animate-spin' : ''} /> Actualizar
            </button>
          </div>

          {loadingUsage ? (
            <div className="flex justify-center py-16"><RefreshCw size={24} className="animate-spin text-orange-400" /></div>
          ) : usage?.success ? (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                <StatCard icon={Activity} label="Total llamadas" value={fmtNum(usage.summary.total_calls)} sub={`${usage.summary.success_rate}% exitosas`} color="blue" />
                <StatCard icon={TrendingUp} label="Caracteres" value={fmtNum(usage.summary.total_chars)} sub={`~${fmtNum(usage.summary.avg_chars_per_call)} chars/llamada`} color="purple" />
                <StatCard icon={DollarSign} label="Costo estimado" value={`$${fmt(usage.summary.total_cost_usd, 4)}`} sub="$0.015 / 1k chars" color="green" />
                <StatCard icon={Check} label="Exitosas" value={fmtNum(usage.summary.success_calls)} color="green" />
                <StatCard icon={AlertCircle} label="Fallidas" value={fmtNum(usage.summary.failed_calls)} color="red" />
                <StatCard icon={Clock} label="Período" value={`${usageDays} días`} sub={`Últimos ${usageDays} días`} color="yellow" />
              </div>

              {/* Charts */}
              {usage.daily?.length > 0 && (
                <div className="glass-panel p-6 rounded-2xl">
                  <h3 className="text-white font-semibold mb-5">Actividad Diaria</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <MiniBarChart data={usage.daily} field="calls" color="#f97316" label="Llamadas/día" />
                    <MiniBarChart data={usage.daily} field="chars" color="#6366f1" label="Caracteres/día" />
                    <MiniBarChart data={usage.daily} field="cost" color="#22c55e" label="Costo USD/día" />
                  </div>
                </div>
              )}

              {/* Top voices */}
              {usage.top_voices?.length > 0 && (
                <div className="glass-panel p-5 rounded-2xl">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Star size={15} className="text-yellow-400" />Voces más usadas</h3>
                  <div className="space-y-2">
                    {usage.top_voices.map((v, i) => (
                      <div key={v.voice_id} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                        <span className="text-gray-600 text-sm w-5">{i + 1}</span>
                        <code className="text-orange-400 text-xs flex-1 truncate">{v.voice_id}</code>
                        <span className="text-gray-400 text-sm font-medium">{fmtNum(v.count)} llamadas</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent events */}
              {usage.recent_events?.length > 0 && (
                <div className="glass-panel p-5 rounded-2xl">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Clock size={15} className="text-gray-400" />Eventos recientes</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-500 text-xs border-b border-white/5">
                          <th className="text-left py-2 pr-4">Fecha</th>
                          <th className="text-right pr-4">Chars</th>
                          <th className="text-right pr-4">Costo</th>
                          <th className="text-right pr-4">Estado</th>
                          <th className="text-left">Voz</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usage.recent_events.slice(0, 20).map((e, i) => (
                          <tr key={i} className="border-b border-white/5 last:border-0 hover:bg-white/2">
                            <td className="py-2 pr-4 text-gray-500 text-xs whitespace-nowrap">{e.ts?.slice(0, 16)?.replace('T', ' ')}</td>
                            <td className="text-right pr-4 text-gray-300">{fmtNum(e.chars)}</td>
                            <td className="text-right pr-4 text-green-400">${fmt(e.cost_usd || 0, 5)}</td>
                            <td className="text-right pr-4">
                              {e.success
                                ? <span className="text-green-400 text-xs">✓</span>
                                : <span className="text-red-400 text-xs">✗</span>}
                            </td>
                            <td className="text-gray-500 text-xs truncate max-w-32">{e.voice_id}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {usage.summary.total_calls === 0 && (
                <div className="glass-panel p-12 rounded-2xl text-center">
                  <BarChart3 size={40} className="text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Sin datos de uso en los últimos {usageDays} días</p>
                  <p className="text-gray-600 text-sm mt-1">Prueba el TTS desde la pestaña "Probar TTS" para generar datos</p>
                </div>
              )}
            </>
          ) : (
            <div className="glass-panel p-8 rounded-2xl text-center">
              <AlertCircle size={32} className="text-red-400 mx-auto mb-3" />
              <p className="text-gray-400">{usage?.error || 'Error cargando estadísticas'}</p>
            </div>
          )}
        </div>
      )}

      {/* ── CONFIG TAB ─────────────────────────────────────────────────────── */}
      {tab === 'config' && config && (
        <div className="space-y-5">
          <div className="glass-panel p-6 rounded-2xl">
            <h3 className="text-white font-semibold mb-5 flex items-center gap-2"><Settings size={16} className="text-orange-400" />Configuración Global de Fish Audio</h3>
            <div className="space-y-4">
              {[
                { key: 'default_voice_id', label: 'Voice ID por defecto', placeholder: 'default (auto)', help: 'Copia el ID de la pestaña "Biblioteca de Voces"' },
                { key: 'notes', label: 'Notas internas', placeholder: 'Notas sobre la configuración…', multiline: true },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-gray-400 text-sm mb-1.5">{f.label}</label>
                  {f.help && <p className="text-gray-600 text-xs mb-2">{f.help}</p>}
                  {f.multiline ? (
                    <textarea rows={3} value={config[f.key] || ''} onChange={e => setConfig(c => ({ ...c, [f.key]: e.target.value }))}
                      placeholder={f.placeholder}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm resize-none focus:outline-none focus:border-orange-500/50" />
                  ) : (
                    <input value={config[f.key] || ''} onChange={e => setConfig(c => ({ ...c, [f.key]: e.target.value }))}
                      placeholder={f.placeholder}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-orange-500/50" />
                  )}
                </div>
              ))}

              <div className="grid grid-cols-3 gap-3">
                {[
                  { key: 'default_latency', label: 'Latencia', opts: [['normal', 'Normal'], ['balanced', 'Balanced']] },
                  { key: 'default_format', label: 'Formato', opts: [['mp3', 'MP3'], ['wav', 'WAV'], ['opus', 'Opus'], ['flac', 'FLAC']] },
                ].map(s => (
                  <div key={s.key}>
                    <label className="block text-gray-400 text-sm mb-1.5">{s.label}</label>
                    <select value={config[s.key] || ''} onChange={e => setConfig(c => ({ ...c, [s.key]: e.target.value }))}
                      className="w-full bg-[#111] border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-orange-500/50">
                      {s.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                ))}
                <div>
                  <label className="block text-gray-400 text-sm mb-1.5">Máx. chars/req</label>
                  <input type="number" min={100} max={4000} value={config.max_chars || 3000}
                    onChange={e => setConfig(c => ({ ...c, max_chars: Number(e.target.value) }))}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-orange-500/50" />
                </div>
              </div>

              <button onClick={saveConfig} disabled={savingConfig}
                className="flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-semibold rounded-xl transition-all">
                {savingConfig ? <RefreshCw size={15} className="animate-spin" /> : <Save size={15} />}
                {savingConfig ? 'Guardando…' : 'Guardar configuración'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
